import React, { useCallback, useMemo, useState } from 'react'

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Locale, format, parse } from 'date-fns'
import { enUS, ja } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  IconButton,
  Separator,
  Stack,
  TableBody,
  Cell as TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TableScrollArea,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronRightRegular,
  FaCloudArrowDownRegular,
} from '@enechain/ecloud-icons'
import {
  downloadCSVFromString,
  generateCsvString,
} from '@enechain/eposition-utils'

import { getTextAlignLightTableStyle } from '../../common/DataTable/styleHelper'
import {
  ColumnId,
  ColumnLabelKey,
  MarginReportTableRow as TableRowData,
  ValueEmphasisKind,
  buildMarginReportCsvRows,
  buildMarginReportTableRows,
  getVisibleColumnConfigs,
  isShortfallRow,
} from './MarginReportTable.utils'
import { MarginReportChartInput } from './types'

type Props = {
  data: MarginReportChartInput[]
  period: 30 | 90
  isHybrid: boolean
}

function formatDisplayDate(iso: string, locale: Locale): string {
  const dateFormat = locale === ja ? 'yyyy/MM/dd（E）' : 'yyyy/MM/dd (EEE)'
  return format(parse(iso, 'yyyy-MM-dd', new Date()), dateFormat, { locale })
}

function formatYen(value: number): string {
  return Math.round(value).toLocaleString('ja-JP')
}

// 金額セルのレンダリング。emphasis 未指定は通常表示。
// - bold: 常に太字（預託金＋履行保証）
// - signed: 負値のみ Text.negativeMid / 太字（余剰/不足、取引可能残枠）
function renderAmountCell(
  value: number | null,
  emphasis?: ValueEmphasisKind,
): React.ReactNode {
  if (value === null) {
    return '-'
  }
  const formatted = formatYen(value)
  if (emphasis === 'bold') {
    return <Text fontWeight="600">{formatted}</Text>
  }
  if (emphasis === 'signed' && value < 0) {
    return (
      <Text color="Text.negativeMid" fontWeight="600">
        {formatted}
      </Text>
    )
  }
  return formatted
}

const columnHelper = createColumnHelper<TableRowData>()

const numericMeta = getTextAlignLightTableStyle()

export const MarginReportTable: React.FC<Props> = ({
  data,
  period,
  isHybrid,
}) => {
  const { t, i18n } = useTranslation(['component'])
  const [isOpen, setIsOpen] = useState(true)

  const dateLocale = i18n.language === 'ja' ? ja : enUS

  const rows: TableRowData[] = useMemo(
    () => buildMarginReportTableRows(data, isHybrid),
    [data, isHybrid],
  )

  const visibleColumnConfigs = useMemo(
    () => getVisibleColumnConfigs(isHybrid),
    [isHybrid],
  )

  const dateRangeText = useMemo(() => {
    if (data.length === 0) {
      return ''
    }
    const firstDate = formatDisplayDate(data[0].date, dateLocale)
    const lastDate = formatDisplayDate(data[data.length - 1].date, dateLocale)
    return t('component:features.margin_report.table.date_range', {
      firstDate,
      lastDate,
      period,
    })
  }, [data, dateLocale, period, t])

  // 列見出しのラベル。列順・ハイブリッド分岐・labelKey は MarginReportTable.utils.ts の
  // COLUMN_CONFIGS（visibleColumnConfigs）が唯一の情報源。ここでは i18n キーを文言に解決するだけ。
  const columnLabel = useCallback(
    (labelKey: ColumnLabelKey): string =>
      t(`component:features.margin_report.table.${labelKey}`),
    [t],
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: t('component:features.margin_report.table.date'),
        cell: (info) => formatDisplayDate(info.getValue(), dateLocale),
        enableSorting: false,
      }),
      ...visibleColumnConfigs.map((columnConfig) =>
        columnHelper.accessor(columnConfig.id, {
          header: columnLabel(columnConfig.labelKey),
          cell: (info) =>
            renderAmountCell(info.getValue(), columnConfig.valueEmphasis),
          enableSorting: false,
          meta: numericMeta,
        }),
      ),
    ],
    [dateLocale, visibleColumnConfigs, t, columnLabel],
  )

  const table = useReactTable<TableRowData>({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleCsvDownload = (): void => {
    const columnOrder: Array<'date' | ColumnId> = [
      'date',
      ...visibleColumnConfigs.map((c) => c.id),
    ]

    const headerRow: Record<'date' | ColumnId, string> = {
      date: t('component:features.margin_report.table.date'),
      ...(Object.fromEntries(
        // 画面表示用の改行は CSV の見出しには含めない
        visibleColumnConfigs.map((c) => [
          c.id,
          columnLabel(c.labelKey).replace(/\n/g, ''),
        ]),
      ) as Record<ColumnId, string>),
    }

    const csvRows = buildMarginReportCsvRows(
      rows,
      visibleColumnConfigs,
      (isoDate) => formatDisplayDate(isoDate, dateLocale),
    )

    const timestamp = format(new Date(), 'yyyyMMddHHmmss')
    downloadCSVFromString(
      generateCsvString(csvRows, columnOrder, headerRow),
      `margin_report_${timestamp}.csv`,
    )
  }

  if (data.length === 0) {
    return null
  }

  return (
    <Box
      backgroundColor="Surface.primary"
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="Border.midEmphasis"
      padding="4"
    >
      <Stack gap="4">
        <Flex
          alignItems="center"
          gap="2"
          cursor="pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <IconButton
            aria-label="toggle"
            backgroundColor="transparent"
            size="sm"
          >
            {isOpen ? <FaChevronDownRegular /> : <FaChevronRightRegular />}
          </IconButton>
          <Text textStyle="headerMd">
            {t(
              isHybrid
                ? 'component:features.margin_report.table.section_title_hybrid'
                : 'component:features.margin_report.table.section_title',
            )}
          </Text>
        </Flex>

        {isOpen && (
          <>
            <Separator />
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" color="Text.highEmphasis">
                {dateRangeText}
              </Text>
              <TertiaryButton
                leftIcon={<FaCloudArrowDownRegular />}
                height="32px"
                disabled={rows.length === 0}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCsvDownload()
                }}
              >
                {t('component:features.margin_report.table.csv_download')}
              </TertiaryButton>
            </Flex>
            <TableScrollArea overflowY="auto" maxHeight="400px">
              <TableRoot
                size="sm"
                css={{ borderCollapse: 'separate', borderSpacing: 0 }}
              >
                <TableHeader position="sticky" top={0} zIndex={1}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableColumnHeader
                          key={header.id}
                          // 列名の文言に含まれる改行（\n）をそのまま折り返して表示する
                          css={{
                            whiteSpace: 'pre-line',
                            ...header.column.columnDef.meta?.headerStyle,
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </TableColumnHeader>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row, index) => (
                    <TableRow
                      key={row.id}
                      backgroundColor={
                        isShortfallRow(row.original)
                          ? 'Surface.negativeLow'
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          css={cell.column.columnDef.meta?.cellStyle}
                          isLast={table.getRowModel().rows.length - 1 === index}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </TableRoot>
            </TableScrollArea>
          </>
        )}
      </Stack>
    </Box>
  )
}
